import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography
} from '@mui/material';

const DEFAULT_BG_RECT = { x: 0, y: 0, width: 1, height: 1 };
const DEFAULT_CHAR_RECT = { x: 0.25, y: 0.12, width: 0.5, height: 0.88 };
/** Full-frame overlay (e.g. transparent PNG vignette at 16:9); shrink with slider if needed */
const DEFAULT_FG_RECT = { x: 0, y: 0, width: 1, height: 1 };

const SceneForm = ({ open, onClose, onSubmit, characters = [], flows = [], submitting = false }) => {
  const characterList = Array.isArray(characters) ? characters : [];
  const flowList = Array.isArray(flows) ? flows : [];

  const [name, setName] = useState('');
  const [characterId, setCharacterId] = useState('');
  const [flowId, setFlowId] = useState('');
  const [flow2Id, setFlow2Id] = useState('');
  const [flow3Id, setFlow3Id] = useState('');

  useEffect(() => {
    if (open) {
      setName('');
      setCharacterId('');
      setFlowId('');
      setFlow2Id('');
      setFlow3Id('');
    }
  }, [open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      backgroundAssetId: null,
      foregroundAssetId: null,
      characterId: characterId || null,
      flowId: flowId || null,
      flow2Id: flow2Id || null,
      flow3Id: flow3Id || null,
      backgroundRect: DEFAULT_BG_RECT,
      characterRect: DEFAULT_CHAR_RECT,
      foregroundRect: DEFAULT_FG_RECT
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create scene</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Background and foreground are set on the layout screen after you create the scene.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Scene name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Character (optional)"
                value={characterId}
                onChange={(e) => setCharacterId(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {characterList.map((c) => (
                  <MenuItem key={c._id} value={String(c._id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Flow (optional)"
                value={flowId}
                onChange={(e) => setFlowId(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {flowList.map((f) => (
                  <MenuItem key={f._id} value={String(f._id)}>
                    {f.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Second flow (optional)"
                value={flow2Id}
                onChange={(e) => setFlow2Id(e.target.value)}
                helperText="Same scene is used in-game for all linked flows"
              >
                <MenuItem value="">None</MenuItem>
                {flowList.map((f) => (
                  <MenuItem key={f._id} value={String(f._id)}>
                    {f.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Third flow (optional)"
                value={flow3Id}
                onChange={(e) => setFlow3Id(e.target.value)}
                helperText="Same scene is used in-game for all linked flows"
              >
                <MenuItem value="">None</MenuItem>
                {flowList.map((f) => (
                  <MenuItem key={f._id} value={String(f._id)}>
                    {f.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SceneForm;
